// PhotoEditor.jsx – Bildausschnitt wählen, drehen, Farben anpassen
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  rotatedBox, clampEdit, defaultEdit, editFromFace, layoutPercent, loadImage,
  autoAdjust, detectFace, DEFAULT_ADJUST, PRINT_PX_GOOD, PRINT_PX_MIN,
} from '../utils/photoEdit';
import './PhotoEditor.css';

function useViewportSize() {
  // Seitenlänge des Ausschnitts: die Bühne (Ausschnitt + 14 % Rand auf jeder Seite = 1,28 ×)
  // muss ganz ins Fenster passen – Fenster max. 560 px, Rand aussen 2 × 10 px, innen 2 × 18 px
  const get = () => {
    const w = window.innerWidth, h = window.innerHeight;
    const inner = Math.min(560, w - 20) - 36;
    return Math.floor(Math.max(150, Math.min(380, inner / 1.28, h - 360)));
  };
  const [v, setV] = useState(get);
  useEffect(() => {
    const on = () => setV(get());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return v;
}

export const RETOUCH_PRICE = 5; // CHF pro Bild, einmalig

// retouch:     { note } wenn das Bild von Hand bearbeitet werden soll, sonst null
// retouchMode: 'edit' (Kunde wählt) | 'view' (Admin sieht den Wunsch) | 'off'
export function PhotoEditor({ photo, edit: initialEdit, retouch: initialRetouch = null, retouchMode = 'edit', title = 'Foto bearbeiten', onConfirm, onCancel }) {
  const [img, setImg] = useState(null);
  const [retouch, setRetouch] = useState(initialRetouch || null);
  const [showRetouchInfo, setShowRetouchInfo] = useState(false);
  const [edit, setEdit] = useState(null);
  const [rot90, setRot90] = useState(0);
  const [fine, setFine] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [autoActive, setAutoActive] = useState(false);
  const [faceStatus, setFaceStatus] = useState(null); // 'searching' | 'found' | 'none'
  const touchedRef = useRef(false);
  const V = useViewportSize();
  const P = Math.round(V * 0.14); // sichtbarer Rand ausserhalb des Ausschnitts

  // Bild laden, Startausschnitt bestimmen
  useEffect(() => {
    let cancelled = false;
    loadImage(photo).then(async (im) => {
      if (cancelled) return;
      setImg(im);
      const w = im.naturalWidth, h = im.naturalHeight;
      if (initialEdit && initialEdit.w === w && initialEdit.h === h) {
        const r = Math.round(initialEdit.rot / 90) * 90;
        setRot90(r);
        setFine(Math.round((initialEdit.rot - r) * 10) / 10);
        setEdit(clampEdit(initialEdit));
        return;
      }
      setEdit(defaultEdit(w, h));
      setFaceStatus('searching');
      const face = await detectFace(im);
      if (cancelled) return;
      if (face && !touchedRef.current) {
        setEdit(editFromFace(w, h, face));
        setFaceStatus('found');
      } else {
        setFaceStatus(face ? null : 'none');
      }
    }).catch(() => setFaceStatus('none'));
    return () => { cancelled = true; };
  }, [photo]); // eslint-disable-line

  const box = useMemo(() => (edit ? rotatedBox(edit.w, edit.h, edit.rot) : null), [edit]);
  const maxS = box ? Math.min(box.rw, box.rh) : 1;
  const minS = Math.min(maxS, Math.max(80, maxS * 0.08));
  const zoom = edit ? maxS / edit.cs : 1;
  const maxZoom = maxS / minS;

  const update = useCallback((fn) => {
    touchedRef.current = true;
    setEdit(e => (e ? clampEdit(fn(e)) : e));
  }, []);

  // Drehung ändern: Ausschnitt-Mitte relativ beibehalten
  const applyRotation = useCallback((newRot) => {
    touchedRef.current = true;
    setEdit(e => {
      if (!e) return e;
      const oldBox = rotatedBox(e.w, e.h, e.rot);
      const newBox = rotatedBox(e.w, e.h, newRot);
      const fx = (e.cx + e.cs / 2) / oldBox.rw, fy = (e.cy + e.cs / 2) / oldBox.rh;
      // bei 90°-Drehung wandert die Mitte mit
      const turn = Math.round((newRot - e.rot) / 90) % 4;
      let nx = fx, ny = fy;
      if (turn === 1 || turn === -3) { nx = 1 - fy; ny = fx; }
      else if (turn === -1 || turn === 3) { nx = fy; ny = 1 - fx; }
      else if (Math.abs(turn) === 2) { nx = 1 - fx; ny = 1 - fy; }
      const cs = Math.min(e.cs, Math.min(newBox.rw, newBox.rh));
      return clampEdit({ ...e, rot: newRot, cs, cx: nx * newBox.rw - cs / 2, cy: ny * newBox.rh - cs / 2 });
    });
  }, []);

  const rotate90 = (dir) => { const r = rot90 + dir * 90; setRot90(r); applyRotation(r + fine); };
  const setFineRot = (v) => { setFine(v); applyRotation(rot90 + v); };

  const setZoom = (z) => update(e => {
    const cs = maxS / z;
    const mx = e.cx + e.cs / 2, my = e.cy + e.cs / 2;
    return { ...e, cs, cx: mx - cs / 2, cy: my - cs / 2 };
  });

  // ── Verschieben / Zoomen mit Maus, Finger, Mausrad ──
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const onPointerDown = (ev) => {
    ev.currentTarget.setPointerCapture(ev.pointerId);
    pointers.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    startGesture();
  };
  const startGesture = () => {
    const pts = [...pointers.current.values()];
    if (!edit) return;
    if (pts.length === 1) gesture.current = { type: 'pan', x: pts[0].x, y: pts[0].y, e: edit };
    else if (pts.length >= 2) {
      const d = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y) || 1;
      gesture.current = { type: 'pinch', d, e: edit };
    }
  };
  const onPointerMove = (ev) => {
    if (!pointers.current.has(ev.pointerId) || !gesture.current) return;
    pointers.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    const g = gesture.current;
    const pts = [...pointers.current.values()];
    const f = V / g.e.cs;
    if (g.type === 'pan' && pts.length === 1) {
      const dx = pts[0].x - g.x, dy = pts[0].y - g.y;
      update(() => ({ ...g.e, cx: g.e.cx - dx / f, cy: g.e.cy - dy / f }));
    } else if (g.type === 'pinch' && pts.length >= 2) {
      const d = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y) || 1;
      const cs = g.e.cs * (g.d / d);
      const mx = g.e.cx + g.e.cs / 2, my = g.e.cy + g.e.cs / 2;
      update(() => ({ ...g.e, cs, cx: mx - cs / 2, cy: my - cs / 2 }));
    }
  };
  const onPointerUp = (ev) => {
    pointers.current.delete(ev.pointerId);
    gesture.current = null;
    if (pointers.current.size) startGesture();
  };
  const stageRef = useRef(null);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (ev) => {
      ev.preventDefault();
      const factor = ev.deltaY > 0 ? 1.08 : 1 / 1.08;
      update(e => {
        const cs = e.cs * factor;
        const mx = e.cx + e.cs / 2, my = e.cy + e.cs / 2;
        return { ...e, cs, cx: mx - cs / 2, cy: my - cs / 2 };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [update, img]);

  const doAuto = () => {
    if (!img || !edit) return;
    if (autoActive) {
      setEdit(e => ({ ...e, ...DEFAULT_ADJUST }));
      setAutoActive(false);
      return;
    }
    const adj = autoAdjust(img, edit);
    setEdit(e => ({ ...e, ...adj }));
    setAutoActive(true);
  };
  const setAdjust = (key, v) => { setAutoActive(false); setEdit(e => ({ ...e, [key]: v })); };
  const resetAll = () => {
    setRot90(0); setFine(0); setAutoActive(false);
    setEdit(e => (e ? defaultEdit(e.w, e.h) : e));
  };

  // Abmessungen für die Darstellung
  let layer = null, imgStyle = null;
  if (edit && box) {
    const f = V / edit.cs;
    layer = { left: P - edit.cx * f, top: P - edit.cy * f, width: box.rw * f, height: box.rh * f };
    imgStyle = layoutPercent(edit).img;
  }
  const cropPx = edit ? Math.round(edit.cs) : 0;
  const quality = cropPx >= PRINT_PX_GOOD ? 'good' : cropPx >= PRINT_PX_MIN ? 'ok' : 'low';

  return (
    <div className="pe-overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="pe-modal" role="dialog" aria-label={title}>
        <div className="pe-head">
          <h3>{title}</h3>
          <button className="pe-close" onClick={onCancel} aria-label="Schliessen">×</button>
        </div>

        <div
          ref={stageRef}
          className="pe-stage"
          style={{ width: V + 2 * P, height: V + 2 * P }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {!img && <div className="pe-loading">Foto wird geladen…</div>}
          {layer && (
            <div style={{ position: 'absolute', ...layer }}>
              <img src={photo} alt="" draggable={false} style={{ position: 'absolute', maxWidth: 'none', pointerEvents: 'none', userSelect: 'none', ...imgStyle }} />
            </div>
          )}
          {/* Abdunkelung ausserhalb des Ausschnitts */}
          <div className="pe-dim" style={{ left: 0, top: 0, right: 0, height: P }} />
          <div className="pe-dim" style={{ left: 0, bottom: 0, right: 0, height: P }} />
          <div className="pe-dim" style={{ left: 0, top: P, width: P, height: V }} />
          <div className="pe-dim" style={{ right: 0, top: P, width: P, height: V }} />
          <div className="pe-crop" style={{ left: P, top: P, width: V, height: V }}>
            <span className="pe-grid pe-grid--v1" /><span className="pe-grid pe-grid--v2" />
            <span className="pe-grid pe-grid--h1" /><span className="pe-grid pe-grid--h2" />
          </div>
        </div>

        <div className="pe-hint">
          {faceStatus === 'searching' && 'Gesicht wird gesucht…'}
          {faceStatus === 'found' && 'Ausschnitt auf das Gesicht ausgerichtet – zum Ändern verschieben oder zoomen.'}
          {(faceStatus === 'none' || faceStatus === null) && 'Zum Ändern das Foto verschieben oder zoomen.'}
        </div>

        <div className="pe-row">
          <span className="pe-icon" aria-hidden>−</span>
          <input type="range" min={1} max={Math.max(1.01, maxZoom)} step={0.01} value={Math.min(zoom, maxZoom)}
            onChange={e => setZoom(Number(e.target.value))} aria-label="Zoom" className="pe-range" />
          <span className="pe-icon" aria-hidden>+</span>
        </div>

        <div className="pe-buttons">
          <button className="pe-btn" onClick={() => rotate90(-1)} title="Nach links drehen">⟲ Drehen</button>
          <button className="pe-btn" onClick={() => rotate90(1)} title="Nach rechts drehen">Drehen ⟳</button>
          <button className={`pe-btn pe-btn--auto${autoActive ? ' pe-btn--active' : ''}`} onClick={doAuto} disabled={!img}>
            {autoActive ? '✓ Automatisch verbessert' : '✨ Automatisch verbessern'}
          </button>
        </div>

        <button className="pe-advanced-toggle" onClick={() => setShowAdvanced(s => !s)}>
          {showAdvanced ? '▾' : '▸'} Erweiterte Einstellungen
        </button>
        {showAdvanced && edit && (
          <div className="pe-advanced">
            <label><div className="pe-lab"><span>Helligkeit</span><span>{Math.round(edit.b * 100)} %</span></div>
              <input type="range" min={0.5} max={1.5} step={0.01} value={edit.b} onChange={e => setAdjust('b', Number(e.target.value))} />
            </label>
            <label><div className="pe-lab"><span>Kontrast</span><span>{Math.round(edit.c * 100)} %</span></div>
              <input type="range" min={0.5} max={1.6} step={0.01} value={edit.c} onChange={e => setAdjust('c', Number(e.target.value))} />
            </label>
            <label><div className="pe-lab"><span>Sättigung</span><span>{Math.round(edit.s * 100)} %</span></div>
              <input type="range" min={0} max={2} step={0.01} value={edit.s} onChange={e => setAdjust('s', Number(e.target.value))} />
            </label>
            <label><div className="pe-lab"><span>Gerade richten</span><span>{fine > 0 ? '+' : ''}{fine}°</span></div>
              <input type="range" min={-15} max={15} step={0.5} value={fine} onChange={e => setFineRot(Number(e.target.value))} />
            </label>
            <div className="pe-advanced-actions">
              <button className="pe-link" onClick={() => setAdjust('s', 0)}>Schwarzweiss</button>
              <button className="pe-link" onClick={resetAll}>Alles zurücksetzen</button>
            </div>
          </div>
        )}

        {retouchMode === 'edit' && (
          <div className={`pe-retouch${retouch ? ' pe-retouch--on' : ''}`}>
            <div className="pe-retouch-row">
              <button
                type="button"
                className={`pe-btn pe-btn--retouch${retouch ? ' pe-btn--retouch-on' : ''}`}
                aria-pressed={!!retouch}
                onClick={() => setRetouch(r => (r ? null : { note: '' }))}
              >
                {retouch ? '✓ Wird von Hand bearbeitet' : '✋ Von Hand bearbeiten lassen'}
                <span className="pe-retouch-price">CHF {RETOUCH_PRICE}.–</span>
              </button>
              <button
                type="button"
                className={`pe-info-btn${showRetouchInfo ? ' pe-info-btn--open' : ''}`}
                onClick={() => setShowRetouchInfo(v => !v)}
                aria-expanded={showRetouchInfo}
                aria-label="Was ist die Bearbeitung von Hand?"
                title="Was ist das?"
              >i</button>
            </div>
            {showRetouchInfo && (
              <div className="pe-retouch-info">
                <strong>Persönliche Bildbearbeitung</strong>
                <p>Ein Mensch aus unserem Team nimmt sich dein Foto persönlich vor und bereitet es sorgfältig für den Druck auf. Je nach Bild und Wunsch zum Beispiel:</p>
                <ul>
                  <li>Ausschnitt, Licht und Farben von Hand abstimmen</li>
                  <li>einen unruhigen Hintergrund bereinigen</li>
                  <li>andere Personen oder störende Gegenstände wegretuschieren</li>
                </ul>
                <p>Kosten: <strong>einmalig CHF {RETOUCH_PRICE}.– pro Bild</strong> – egal, wie viele Exemplare du bestellst. Der Betrag wird mit der Bestellung verrechnet. Sollte ein Wunsch nicht umsetzbar sein, melden wir uns vorher bei dir.</p>
              </div>
            )}
            {retouch && (
              <textarea
                className="pe-retouch-note"
                rows={2}
                maxLength={500}
                value={retouch.note}
                onChange={e => setRetouch({ note: e.target.value })}
                placeholder="Was sollen wir am Bild machen? z.B. «Person rechts entfernen» oder «Hintergrund ruhiger machen» (freiwillig)"
              />
            )}
          </div>
        )}
        {retouchMode === 'view' && initialRetouch && (
          <div className="pe-retouch-view">
            <strong>✋ Der Kunde wünscht eine Bearbeitung von Hand.</strong>
            <div>{initialRetouch.note || 'Keine besonderen Wünsche angegeben.'}</div>
          </div>
        )}

        {edit && quality !== 'good' && (
          <div className={`pe-quality pe-quality--${quality}`}>
            {quality === 'low'
              ? `Achtung: Der Ausschnitt hat nur ${cropPx} × ${cropPx} Pixel und wird im Druck unscharf. Wähle einen grösseren Ausschnitt oder ein Foto in besserer Qualität.`
              : `Hinweis: Der Ausschnitt hat ${cropPx} × ${cropPx} Pixel. Für einen scharfen Druck wären ${PRINT_PX_GOOD} Pixel oder mehr ideal.`}
          </div>
        )}

        <div className="pe-actions">
          <button className="pe-btn pe-btn--ghost" onClick={onCancel}>Abbrechen</button>
          <button className="pe-btn pe-btn--primary" disabled={!edit} onClick={() => onConfirm(edit, retouchMode === 'edit' ? (retouch ? { note: retouch.note.trim() } : null) : initialRetouch)}>Übernehmen</button>
        </div>
      </div>
    </div>
  );
}
